import {
  faChartMixed,
  faCircleExclamation,
  faSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import type { Layout } from "react-grid-layout";

import type { ChartExploreContext } from "src/analytics/copilot/chartExploreContext";
import { DashboardGridSkeleton } from "src/analytics/dashboard/DashboardGridSkeleton";
import { DashboardLayoutEditor } from "src/analytics/dashboard/DashboardLayoutEditor";
import { sortPinsByPosition } from "src/analytics/dashboard/dashboardLayout";
import { StaticPinGrid } from "src/analytics/dashboard/StaticPinGrid";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import type { Realm } from "src/clients/analytics-dashboard";
import { EmptyState } from "src/design-system/EmptyState";
import { WhitePane } from "src/jobs/common/WhitePane";

export const DashboardGrid: React.FC<{
  realm: Realm;
  realmId: string;
  labelDictionaries: LabelDictionaries;
  isEditing?: boolean;
  isSaving?: boolean;
  editLayout?: Layout;
  onEditLayoutChange?: (layout: Layout) => void;
  dashboardId?: string;
  onOpenCopilot?: () => void;
  onExploreWithCopilot?: (structuredInput: ChartExploreContext) => void;
}> = ({
  realm,
  realmId,
  labelDictionaries,
  isEditing = false,
  isSaving = false,
  editLayout,
  onEditLayoutChange,
  dashboardId,
  onOpenCopilot,
  onExploreWithCopilot,
}) => {
  const { dashboard, isLoading, hasLoadFailed } = useDashboard(
    realm,
    realmId,
    dashboardId,
  );
  const pins = sortPinsByPosition(Object.values(dashboard.pins));

  if (isLoading) {
    return <DashboardGridSkeleton />;
  }

  if (hasLoadFailed) {
    return (
      <WhitePane noMinWidth>
        <EmptyState
          dataLoc="dashboard-load-error"
          description="There was an error loading the dashboard. Please try again."
          headline="Error loading dashboard"
          icon={faCircleExclamation}
          variant="error"
        />
      </WhitePane>
    );
  }

  if (pins.length === 0) {
    return (
      <WhitePane noMinWidth>
        <EmptyState
          actionButton={
            onOpenCopilot
              ? {
                  label: "Ask Copilot",
                  icon: faSparkles,
                  onClick: onOpenCopilot,
                }
              : undefined
          }
          description="Describe the charts and KPIs to build, or ask Copilot for a recommendation."
          headline="Add your first chart"
          icon={faChartMixed}
        />
      </WhitePane>
    );
  }

  return isEditing && editLayout && onEditLayoutChange ? (
    <DashboardLayoutEditor
      dashboardId={dashboardId}
      isSaving={isSaving}
      labelDictionaries={labelDictionaries}
      layout={editLayout}
      onLayoutChange={onEditLayoutChange}
      pins={pins}
      realm={realm}
      realmId={realmId}
    />
  ) : (
    <div
      className="decideScrollbar h-full overflow-y-auto"
      data-loc="dashboard-grid"
    >
      <StaticPinGrid
        dashboardId={dashboardId}
        labelDictionaries={labelDictionaries}
        onExploreWithCopilot={onExploreWithCopilot}
        pins={pins}
        realm={realm}
        realmId={realmId}
      />
    </div>
  );
};
